import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { get as commsGet } from "../../../service/comms/commsApi";
import { getTicket, listTickets } from "../../../service/comms/supportService";
import {
  subscribeSupportResync,
  useRoomsUnread,
} from "../../../hooks/comms/RoomsUnreadProvider";
import { useCachedList } from "../../../hooks/useCachedList";
import {
  serializeSupportQueueKey,
  supportQueueCache,
} from "../../../utils/listCache/caches";
import { extractTicketDetail } from "./ticketUtils";
import { normalizeTicket, toList } from "./supportUtils";
import { mergeTicketIntoRows } from "./queueMerge";
import { PSEUDO_VIEWS } from "./supportEnums";

/** How many tickets one page of the list holds. */
const PAGE_SIZE = 100;

/** Coalesce a burst of realtime events into one revalidate. */
const RESYNC_DEBOUNCE_MS = 800;

/**
 * Don't revalidate on window focus if the rows are newer than this. Alt-tabbing
 * used to cost a full 100-row fetch every single time.
 */
const FOCUS_STALE_AFTER_MS = 20000;

/** Empty filter state — every dimension off. */
const NO_FILTERS = {
  department: "",
  type: "",
  priority: "",
  tag: "",
  assignee: "",
  waiting_on: "",
  category: "",
};

/**
 * Match a ticket against the ops-bar search box.
 *
 * **This is a client-side filter over the loaded page only.** `GET
 * /support/tickets` has no `search` param, so a query cannot reach tickets on
 * pages that were never fetched — the same trap `breached` was in before it
 * became a server-side predicate. The list says so in its footer rather than
 * quietly returning a wrong answer.
 *
 * @param {Object} ticket - Normalized ticket.
 * @param {string} query
 * @returns {boolean}
 */
const matchesSearch = (ticket, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    ticket.displayId,
    ticket.subject,
    ticket.client,
    ticket.department,
    ticket.type,
    ...(ticket.tags || []),
  ];
  return hay.some((field) => field != null && String(field).toLowerCase().includes(q));
};

/**
 * The ticket queue behind the support workspace: the active saved view, sort
 * and filters, the loaded page, and how it stays current.
 *
 * Fetching goes through `useCachedList`, which means three things that matter
 * on a queue an agent switches between all day:
 *
 *  - a view they've already opened **paints from cache and revalidates
 *    quietly** — no spinner, no wait, and the rows never blank out
 *  - concurrent consumers of the same key share **one** request
 *  - `mutate()` can splice a single changed ticket into the list, so a realtime
 *    event costs one small fetch instead of re-pulling a hundred rows
 *
 * Everything except the search box is still a server-side predicate: the
 * browser must not re-derive `breached`, `view` or `sort`, because a
 * page-local answer stops agreeing with the sidebar counts the moment the
 * queue exceeds one page.
 *
 * @param {Object} [options]
 * @param {string} [options.initialView]
 * @param {string} [options.search] - Ops-bar query, applied client-side.
 * @returns {Object} Queue state and setters.
 */
export function useSupportQueue({ initialView = "all_open", search = "" } = {}) {
  const [view, setViewState] = useState(initialView);
  const [sort, setSort] = useState("oldest");
  const [filters, setFilters] = useState(NO_FILTERS);
  const [version, setVersion] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const viewTouchedRef = useRef(false);
  const { supportTicketsVersion } = useRoomsUnread();

  const key = serializeSupportQueueKey({ view, sort, filters });

  const fetcher = useCallback(async () => {
    // A pseudo-view is plain query params, not a `?view=` — sending both would
    // be contradictory (e.g. `view=resolved_today&status=resolved`).
    const viewParams = PSEUDO_VIEWS[view] || { view };
    const payload = await listTickets(commsGet, {
      ...viewParams,
      sort,
      limit: PAGE_SIZE,
      // Empty strings mean "no filter" — drop them so buildQuery omits them.
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "")),
    });
    return toList(payload).map(normalizeTicket);
  }, [view, sort, filters]);

  const { data, loading, error, refresh, mutate } = useCachedList({
    cache: supportQueueCache,
    key,
    fetcher,
  });

  const tickets = useMemo(() => data || [], [data]);

  // Follow a late-arriving initialView (the staff probe resolves async) only
  // while the agent hasn't picked a view themselves.
  useEffect(() => {
    if (!viewTouchedRef.current) setViewState(initialView);
  }, [initialView]);

  const setView = useCallback((next) => {
    viewTouchedRef.current = true;
    setViewState(next);
    setSelectedIds(new Set());
  }, []);

  const setFilter = useCallback((filterKey, value) => {
    setFilters((prev) => ({ ...prev, [filterKey]: value }));
    // A row ticked under the old filter is probably off-screen now.
    setSelectedIds(new Set());
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(NO_FILTERS);
    setSelectedIds(new Set());
  }, []);

  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);

  const revalidate = useCallback(() => {
    bumpVersion();
    return refresh();
  }, [bumpVersion, refresh]);

  // --- Realtime: splice one ticket rather than re-pulling the page ----------

  const stateRef = useRef({ view, mutate, revalidate });
  stateRef.current = { view, mutate, revalidate };

  const debounceRef = useRef(null);
  const scheduleRevalidate = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => stateRef.current.revalidate(), RESYNC_DEBOUNCE_MS);
  }, []);

  /**
   * Fold a single changed ticket into the cached list.
   *
   * Deliberately conservative: this is an optimisation over refetching, and
   * `GET /support/views/counts` stays the authority for every number on screen.
   * Whenever membership in the current view isn't obvious — a resolve, an
   * assignment, a breach — it falls through to a debounced revalidate instead
   * of guessing, because client-side view membership is precisely the mistake
   * the old browser-side `breached` filter made.
   *
   * @param {string} roomId
   * @param {string|null} eventType
   */
  const mergeTicket = useCallback(
    async (roomId, eventType) => {
      try {
        const fresh = normalizeTicket(extractTicketDetail(await getTicket(commsGet, roomId)));
        if (!fresh.roomId) {
          scheduleRevalidate();
          return;
        }
        let handled = false;
        stateRef.current.mutate((rows = []) => {
          const result = mergeTicketIntoRows(rows, fresh, {
            view: stateRef.current.view,
            eventType,
          });
          handled = result.handled;
          return result.rows;
        });
        // Not ours to place — let the server decide on the next pass.
        if (!handled) scheduleRevalidate();
        else bumpVersion();
      } catch (err) {
        // The single-ticket read failing is not worth surfacing; the
        // revalidate behind it is the real correctness path.
        scheduleRevalidate();
      }
    },
    [scheduleRevalidate, bumpVersion]
  );

  useEffect(() => {
    return subscribeSupportResync((detail) => {
      if (document.hidden) return; // the focus listener catches up on return
      if (detail?.roomId) {
        mergeTicket(detail.roomId, detail.eventType || null);
        return;
      }
      scheduleRevalidate();
    });
  }, [mergeTicket, scheduleRevalidate]);

  // The provider's periodic sweep is the safety net behind the events above.
  const sweepRef = useRef(supportTicketsVersion);
  useEffect(() => {
    if (supportTicketsVersion === sweepRef.current) return;
    sweepRef.current = supportTicketsVersion;
    if (document.hidden) return;
    scheduleRevalidate();
  }, [supportTicketsVersion, scheduleRevalidate]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Returning to the window is a good staleness signal, but only if the rows
  // have actually aged — otherwise every alt-tab costs a full page.
  useEffect(() => {
    const onFocus = () => {
      const entry = supportQueueCache.peek(key);
      if (entry && Date.now() - entry.ts < FOCUS_STALE_AFTER_MS) return;
      revalidate();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [key, revalidate]);

  // --- Selection ------------------------------------------------------------

  const visible = useMemo(
    () => tickets.filter((t) => matchesSearch(t, search)),
    [tickets, search]
  );

  const toggleSelect = useCallback((roomId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === visible.length ? new Set() : new Set(visible.map((t) => t.roomId))
    );
  }, [visible]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  /** The loaded row for a room id, for seeding the ticket header before detail lands. */
  const selected = useCallback(
    (roomId) => (roomId ? tickets.find((t) => String(t.roomId) === String(roomId)) : null),
    [tickets]
  );

  return {
    view,
    setView,
    sort,
    setSort,
    filters,
    setFilter,
    clearFilters,
    tickets: visible,
    loadedCount: tickets.length,
    searching: search.trim().length > 0,
    loading,
    error: error?.message || null,
    version,
    refresh: revalidate,
    selected,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
  };
}

export default useSupportQueue;
